// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the one notice card for a page or panel that has nothing to list or can't show more
// (JP 2026-11-03: "one card to look after"): Agents (pull request, run, chats and side panels),
// Mail, Tasks and the ClickUp inbox all use it. A small scene of the thing it is about (two
// stacked cards, the front one carrying a tile with the page's icon and a badge for the state),
// the title, the repository it is about, why, what the page shows once there is something, and
// what to do. The Lens material and its values per theme live in PageNotice.module.css (.lens).

import styles from '@app/features/ui/page_notice/PageNotice.module.css';
import {clsx} from 'clsx';
import type React from 'react';

export interface PageNoticePoint {
	icon: React.ReactNode;
	text: string;
}

/** `scene` centred with its picture; `inline` under a known header (tile beside the words);
 *  `compact` in a narrow column such as a side list or panel (tile beside the title). */
export type PageNoticeLayout = 'scene' | 'inline' | 'compact';

export function PageNotice({
	icon,
	badge,
	tone = 'plain',
	title,
	chip,
	text,
	points,
	actions,
	layout = 'scene',
	className,
	...rest
}: Omit<React.HTMLAttributes<HTMLElement>, 'title'> & {
	/** The page's own icon, on the tile. */
	icon: React.ReactNode;
	/** A small mark on the tile's corner: the state (a tick for all caught up, a plug for not connected). */
	badge?: React.ReactNode;
	/** `bad` paints the badge a flat red (it failed); `plain` keeps it ink. */
	tone?: 'bad' | 'plain';
	title: React.ReactNode;
	/** The repository and number it is about, in a code chip. */
	chip?: string;
	text?: React.ReactNode;
	/** What the page shows once there is something, one short line each. */
	points?: ReadonlyArray<PageNoticePoint>;
	actions?: React.ReactNode;
	layout?: PageNoticeLayout;
}) {
	const tile = (
		<span className={styles.tile} aria-hidden>
			{icon}
			{badge && <span className={clsx(styles.badge, tone === 'bad' && styles.badgeBad)}>{badge}</span>}
		</span>
	);
	const actionRow = actions ? <div className={styles.actions}>{actions}</div> : null;
	return (
		<section
			className={clsx(
				styles.notice,
				layout === 'inline' && styles.inline,
				layout === 'compact' && styles.compact,
				className,
			)}
			role="status"
			{...rest}
		>
			{layout === 'scene' ? (
				<div className={styles.scene} aria-hidden>
					<span className={clsx(styles.ghost, styles.ghostBack)} />
					<span className={styles.ghost}>
						{tile}
						<span className={styles.ghostLines}>
							<i />
							<i />
						</span>
					</span>
				</div>
			) : (
				tile
			)}
			<div className={styles.body}>
				<h2 className={styles.title}>{title}</h2>
				{chip && <code className={styles.chip}>{chip}</code>}
				{text && <p className={styles.text}>{text}</p>}
				{points && points.length > 0 && (
					<ul className={styles.points}>
						{points.map((point) => (
							<li key={point.text}>
								<span className={styles.pointIcon} aria-hidden>
									{point.icon}
								</span>
								{point.text}
							</li>
						))}
					</ul>
				)}
				{layout !== 'compact' && actionRow}
			</div>
			{layout === 'compact' && actionRow}
		</section>
	);
}

/** Where a notice card sits: centred in what is left of an empty page, or `inline` at the top of a
 *  column under a known header. Hosts may pad it with their own class. */
export function PageNoticeArea({
	inline = false,
	className,
	children,
}: {
	inline?: boolean;
	className?: string;
	children: React.ReactNode;
}) {
	return <div className={clsx(styles.area, inline && styles.areaInline, className)}>{children}</div>;
}

/** A notice button: `ink` for the one thing to do (Connect, Try again), plain for a way on. */
export function PageNoticeButton({
	ink = false,
	className,
	type = 'button',
	...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {ink?: boolean}) {
	return <button type={type} className={clsx(styles.button, ink && styles.buttonInk, className)} {...rest} />;
}

/** A notice link that looks like its buttons (Open on GitHub, Open in Outlook). */
export function PageNoticeLink({
	ink = false,
	className,
	...rest
}: React.AnchorHTMLAttributes<HTMLAnchorElement> & {ink?: boolean}) {
	return <a className={clsx(styles.button, ink && styles.buttonInk, className)} {...rest} />;
}

/** The Lens material's values per theme, for a host that draws wells, tiles or skeletons. */
export const lensClassName = styles.lens;
